import { useEffect, useRef } from 'react';
import postal from 'postal';

import useFunctionKeys from '../../hooks/useFunctionKeys';
import { getListJobNameByJobName } from '../../components/common/listJobNames';
import { JobNames } from '../../framework.core/JobNames';
import { useHistory } from 'react-router';
import useDeepCompareEffect from '../../hooks/useDeepCompareEffect';

interface IndexByAny {
  [key: string]: any,
};

export const ColumnMemory: IndexByAny = {};
const ScrollMemory: IndexByAny = {};

// Without notification timeout, job list view performance could be affected when selection is changed
// Currently, job list view SmartTable is protected with useMemo so it is safe to turn off the timeout.
// const NotificationTimeout = 200;
const ClickHandlerNodeDepthSearch = 5;

function updateScrollMemory(tableId: string) {
  const tableNode = document.getElementById(tableId) as HTMLElement;
  if (!ScrollMemory[tableId]) {
    ScrollMemory[tableId] = {};
  }

  ScrollMemory[tableId].left = tableNode.scrollLeft;
  ScrollMemory[tableId].top = tableNode.scrollTop;
}

function findClickedCell(e: MouseEvent, tableId: string) {
  if (!e || !e.target) {
    return null;
  }

  const cellIdPrefix = `${tableId}-cell-`;

  let node = e.target as Element;
  for (let i = 0; i < ClickHandlerNodeDepthSearch; ++i) {
    if (!node) {
      break;
    }

    if (node.id && node.id.startsWith(cellIdPrefix)) {
      return node;
    }

    node = node.parentNode as Element;
  }

  return null;
}

function parseCellLocation(cellId: string) {
  const parts = cellId.split('-');

  let column = -1;
  let row = -1;

  if (parts.length < 2) {
    // Should never happen
    return { column, row };
  }
  column = parseInt(parts[parts.length - 1]);
  row = parseInt(parts[parts.length - 2]);

  return { column, row };
}

function parseRowLocation(rowId: string) {
  const parts = rowId.split('-');

  let row = -1;

  if (parts.length < 2) {
    // Should never happen
    return row;
  }
  row = parseInt(parts[parts.length - 1]);

  return row;
}

function scrollCellIntoView(tableId: string, cellNode: Element, holdScroll: boolean, ifmsChannel: IChannelDefinition<unknown>, useVirtual: boolean, isLookup:boolean) {
  if (!ScrollMemory[tableId]) updateScrollMemory(tableId); //Initialize ScrollMemory

  const tableNode = document.getElementById(tableId);
  const header = document.getElementById(`${tableId}-header-row`);
  if (!tableId || !header || !tableNode) {
    return;
  }

  const hrect = header.getBoundingClientRect();
  const crect = cellNode.getBoundingClientRect();
  const prect = (tableNode.parentNode as Element).getBoundingClientRect();

  const { xDelta, yDelta } = isCellScrolledIntoView(tableNode, crect, hrect, prect, tableId, useVirtual, isLookup);

  if (tableNode && !holdScroll) {
    let scrollToOptions = {
      top: tableNode.scrollTop + yDelta,
      left: tableNode.scrollLeft + xDelta,
      behavior: 'smooth' as ScrollBehavior
    }

    tableNode.scrollTo(scrollToOptions);
    updateScrollMemory(tableId);

    ScrollMemory[tableId].selectedRowScrollPosition = scrollToOptions.top;
  }
  else
    ScrollMemory[tableId].selectedRowScrollPosition = tableNode.scrollTop;
}

/**
 * Page Up, Page Down 키 입력 시 이동할 행 수를 계산하기 위한 함수.
 * @param tableId table Id - List Table
 * @param row row index
 * @param col colIndex
 * @param isPageUp PageUp > true / PageDown > false
 * @returns 
 */
function pageMoveCellCount(tableId: string, row: number, col: number, isPageUp: boolean = false) {
  const tableNode = document.getElementById(tableId);
  if (!tableId || !tableNode) {
    return 0;
  }
  const cellId = `${tableId}-cell-${row}-${col}`;
  const cell = document.getElementById(cellId);
  const crect = cell?.getBoundingClientRect();
  const scrollWidth = 18;

  // page up, down시 이동해야 할 row 수를 계산하기 위함.
  if (crect) {
    //  이동해야 할 row 계산식: 현재 보이는 화면 height / cell height
    let totalCount = Math.floor((tableNode.clientHeight - scrollWidth) / crect.height);

    if (!isPageUp) {
      let tableAllRows = document.querySelectorAll('[role="row"]');
      let lastRow = tableAllRows[tableAllRows.length - 1];
      let lastRowIndex = parseRowLocation(lastRow.id);

      // 이동할 위치보다 마지막 row 위치가 더 작으면 이면 마지막 행을 선택하게 함.
      if (lastRowIndex < row + totalCount) {
        return lastRowIndex - row;
      }
    } else {
      // 이동할 위치보다 row 위치가 더 작으면 첫 행을 선택하게 함.
      if (row < totalCount) {
        return row;
      }
    }

    return totalCount;
  }

  return 0;
}

/**
 * 
 * @param tableNode 
 * @param crect     child rectangles - cell. 
 * @param hrect     header rectangles
 * @param prect     parent rectangles - table div
 * @param tableId 
 * @returns 
 */
function isCellScrolledIntoView(tableNode: Element, crect: DOMRect, hrect: DOMRect, prect: DOMRect, tableId: string, useVirtual: boolean, isLookup: boolean) {
  const scrollWidth = 18;
  const listScrollPos = ScrollMemory[tableId];    // 사용자가 스크롤 했던 마지막 scroll position
  // x
  const xEnd = prect.left + prect.width - scrollWidth;
  const xVisible = crect.left >= prect.left && ((crect.left + crect.width) <= xEnd);
  // left or right에 cell pos 고정하기 위한 변수
  const moveScrollX = useVirtual && (tableNode.scrollLeft !== (listScrollPos?.left ?? 0));
  // memory에 있는 scroll 위치로 이동 시, cell이 보이는지 확인.
  const xNotVisibleScrollMemory = prect.left + listScrollPos?.left > crect.left;

  // y
  const yStart = prect.top + hrect.height;
  const yEnd = prect.top + tableNode.clientHeight;
  const yVisible = crect.top >= yStart && (crect.top + crect.height) <= yEnd;

  // tab 이동 후 - top or bottom에 cell 표시가 필요한지 확인하기 위함.
  const moveScrollY = useVirtual && (tableNode.scrollTop !== (listScrollPos?.top ?? 0));
  // memory에 있는 scroll 위치로 이동 시, cell이 보이는지 확인.
  const yVisibleScrollMemoryBottom = (crect.top + crect.height) <= (yEnd + listScrollPos?.top);
  const yNotVisibleScrollMemoryTop = (yStart + listScrollPos?.top) > crect.top;

  let xDelta = 0;
  let yDelta = 0;

  if (!xVisible) {
    if (moveScrollX) {
      if (xNotVisibleScrollMemory)
        xDelta = crect.left - prect.left;
      else
        xDelta = listScrollPos?.left ?? 0;
    }
    if (prect.left > crect.left) {
      xDelta = -Math.abs(prect.left - crect.left);
    } else {
      xDelta = (crect.left + crect.width) - (prect.left + prect.width - scrollWidth);
    }
  }

  if (isLookup) { // Lookup Table에서 키보드 Typing 했을 때 일치하는 데이터가 가장 위에 표시되도록
    yDelta = crect.top - crect.height - prect.top;
  }
  else {
    if (!yVisible) {
      if (moveScrollY) {
        if (yNotVisibleScrollMemoryTop) {
          yDelta = crect.top - yStart - tableNode.scrollTop
        }
        else if (yVisibleScrollMemoryBottom) {
          yDelta = listScrollPos?.top - tableNode.scrollTop
        }
        else
          yDelta = crect.top - prect.top - tableNode.clientHeight + hrect.height - tableNode.scrollTop;
      } else if (crect.top < yStart) {
        yDelta = -Math.abs(yStart - crect.top);
      } else {
        yDelta = (crect.top + crect.height) - yEnd;
      }
    }
  }

  return { xDelta, yDelta };
}

function getDefaultColumnSelectionIndex(tableId: string, tableColumnDefaultFocusHierarchy: Array<any>) {
  if (!tableColumnDefaultFocusHierarchy || tableColumnDefaultFocusHierarchy.length <= 0) {
    return -1;
  }

  const table = document.getElementById(tableId);
  if (!table) {
    return -1;
  }

  for (let i = 0; i < tableColumnDefaultFocusHierarchy.length; ++i) {
    const t = tableColumnDefaultFocusHierarchy[i];
    const headers = table.querySelectorAll(`[data-column-id='${t}']`);
    if (headers && headers.length > 0) {
      const header = headers[0];
      const parts = header.id.split('-');
      if (parts.length > 0) {
        return parseInt(parts[parts.length - 1]);
      }
    }
  }

  return -1;
}

function getChildrenTree(x: HTMLElement, result: Array<HTMLElement>) {
  if (!x) {
    return;
  }

  const children = x.childNodes;
  if (children.length <= 0) {
    return;
  }

  children.forEach(x => {
    result.push(x as HTMLElement);
    getChildrenTree(x as HTMLElement, result);
  });
}

function getCellId(tableId: string, col: number) {
  const headerId = `${tableId}-header-${col}`;
  const header = document.getElementById(headerId);
  return header?.dataset?.columnId;
}

function getCellText(tableId: string, row: number, col: number) {
  const cellId = `${tableId}-cell-${row}-${col}`;
  const cell = document.getElementById(cellId);
  if (!cell) {
    return '';
  }

  // Check for check mark icons
  if (cell.getElementsByClassName('ifms-check-box-icon-true').length > 0 || cell.getElementsByClassName('e-ifms-checkbox').length > 0) {
    return true;
  }
  if (cell.getElementsByClassName('ifms-check-box-icon-false').length > 0 || cell.getElementsByClassName('e-ifms-checkbox-unchecked').length > 0) {
    return false;
  }

  // Check for date/dateTime
  if (cell.getElementsByClassName('ifms-table-date').length > 0) {
    return cell.innerText ?? '';
    //return '';
  }
  if (cell.getElementsByClassName('ifms-table-datetime').length > 0) {
    return cell.innerText ?? '';
    //return '';
  }
  if (cell.getElementsByClassName('ifms-table-datetimesec').length > 0) {
    return cell.innerText ?? '';
    //return '';
  }

  // Check for customized value (for example custom renderers)
  let actualCellValue: string | null = null;
  if (cell.dataset?.cellActualValue) {
    actualCellValue = cell.dataset.cellActualValue;
  }
  if (!actualCellValue) {
    const childNodes: Array<HTMLElement> = [];
    getChildrenTree(cell, childNodes);

    const childWithActualValue = childNodes.find(x => !!x.dataset?.cellActualValue);
    if (childWithActualValue) {
      actualCellValue = childWithActualValue.dataset.cellActualValue ?? '';
    }
  }

  if (actualCellValue) {
    return actualCellValue;
  }

  if ([0,2].includes(col) && cell.getElementsByTagName('img')[0]?.id && cell.getElementsByTagName('div')[1].id === 'taskStatus_renderer' ) //taskStatus
    return cell.getElementsByTagName('img')[0].id === 'redpinIcon' ? 'Pending' : cell.getElementsByTagName('img')[0].id === 'greenpinIcon' ? 'Done' : 'GPS'

  if (col === 0 && cell.getElementsByTagName('div')[1].id === 'aiAgent_renderer' ){      //ISF-AiAgent
     if(cell.getElementsByTagName('img')[0]?.id && cell.getElementsByTagName('img')[0].id === 'sparkleIcon') return true;
     return false;
  } 

  if ([0,2].includes(col) && cell.getElementsByTagName('svg')[0]?.id && cell.getElementsByTagName('div')[1].id === 'schStatus_renderer' ) //schedule Status
    return cell.getElementsByTagName('svg')[0].id === 'active' ? 'Active' : cell.getElementsByTagName('svg')[0].id === 'paused' ? 'Paused' : 'Completed'

  if (col === 0 && cell.getElementsByTagName('img')[0]?.id) //status
    return cell.getElementsByTagName('img')[0].id === 'blueIcon' ? 'OC' : cell.getElementsByTagName('img')[0].id === 'blackIcon' ? 'AC' : 'PC'
  
  if (col === 1 && cell.getElementsByTagName('div')[1].id === 'recognized_renderer') //recognize
    return 'R';

  return cell.innerText.split('\n')[0].replace('\r', '');
}

function getCellTooltip(tableId: string, row: number, col: number): string | null {
  const cellId = `${tableId}-cell-${row}-${col}`;
  const cell = document.getElementById(cellId);
  
  if (!cell) {
    return null;
  }

  const childNodes: Array<HTMLElement> = [];
  getChildrenTree(cell, childNodes);

  const childWithTooltip = childNodes.find(x => !!x.dataset?.tooltip);
  if (childWithTooltip) {
    return childWithTooltip.dataset.tooltip ?? null;
  }

  return null;
}

export default function useTableNavigation(
  tableId: string,
  onRowIndexChanged: any,
  onRowDoubleClick: any,
  tableColumnDefaultFocusHierarchy: Array<any>,
  skipObserver: boolean = false,
  dontHighlightCell: boolean = false,
  deselectable: boolean = false,
  executeSameFuncDoubleClickAndEnter: boolean = false,
  onCellRightClick: ((row: number, column: number, node: Element, e:MouseEvent) => void) | undefined = undefined,
  initialized: boolean = false,
  doNotSetRowBackColor:boolean = false,
  eventChangeTrigger: any[] = [], // initialize() 재호출 
  scopedKeydown:boolean = false, // true = View안에 Table이 여러 개인 경우, 각 Table에 마우스 클릭으로 Focus줬을 때 KeyDown이벤트가 동작
) {
  const table = document.getElementById(tableId) as HTMLElement;

  const isListView = tableId?.includes('tbl-list-');
  const useVirtual = tableId?.includes('batchRecognitionReportTable') || tableId?.includes('-lookuptable');

  const channel = postal.channel('ifms-filter');
  const iFMSChannel = postal.channel('ifms');

  useFunctionKeys({ onFunction, onEnter: onEnterKey });

  let notifiationTimer: ReturnType<typeof setTimeout> | null = null;

  const selectedRow = useRef(-1);
  const _selectedCol = useRef(ColumnMemory[tableId] ?? -1);
  let presetSubscription: ISubscriptionDefinition<any> | null = null;

  function subscribeChannels() {
    presetSubscription = postal.subscribe({
      channel: 'ifms-filter',
      topic: 'ifms-filter.filterPreset',
      callback: () => onFunction('F5'),
    });
  }

  function unsubscribeChannels() {
    if (presetSubscription) {
      postal.unsubscribe(presetSubscription);
    }
  }

  useDeepCompareEffect(() => {
    initialize();

    return uninitialize;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, table, eventChangeTrigger]);

  function isNotUseStatusSearch() {
    if (getSelectedCol() !== 0)
      return false;

    const jobNameList: string[] = [];
    jobNameList.push(getListJobNameByJobName(JobNames.OELoad) ?? '');         //OE: Load Plan
    // jobNameList.push(getListJobNameByJobName(JobNames.OEContainer) ?? '');    //OE: Container Status
    jobNameList.push(getListJobNameByJobName(JobNames.OILoad) ?? '');         //OI: Load Plan
    // jobNameList.push(getListJobNameByJobName(JobNames.OIContainer) ?? '');    //OI: Container Status
    jobNameList.push(getListJobNameByJobName(JobNames.OIQuotation) ?? '');    // OI : OI Quotation
    jobNameList.push(getListJobNameByJobName(JobNames.OEQuotation) ?? '');    // OE : OE Quotation
    jobNameList.push(getListJobNameByJobName(JobNames.AIQuotation) ?? '');    // AI : AI Quotation
    jobNameList.push(getListJobNameByJobName(JobNames.AEQuotation) ?? '');    // AE : AE Quotation
    jobNameList.push(getListJobNameByJobName(JobNames.WHRcpt) ?? '');         //OT: W/H Receipt
    jobNameList.push(getListJobNameByJobName(JobNames.WHInvenSummary) ?? ''); //Warehousing: Inventory
    jobNameList.push(getListJobNameByJobName(JobNames.WHLoc) ?? '');          //Warehousing: Location
    jobNameList.push(getListJobNameByJobName(JobNames.WHItem) ?? '');          //Warehousing: Item
    jobNameList.push(getListJobNameByJobName(JobNames.WHCustomer) ?? '');     //Warehousing: Customer
    jobNameList.push(getListJobNameByJobName(JobNames.WHInvenDetail) ?? '');  //Warehousing: In/Out Activity
    jobNameList.push(getListJobNameByJobName(JobNames.Company) ?? '');          //Customer List
    jobNameList.push(getListJobNameByJobName(JobNames.BankRecon) ?? '');        //Accounting: Bank Recon
    jobNameList.push(getListJobNameByJobName(JobNames.OtQuotation) ?? '');            //OT: Quotation
    jobNameList.push(getListJobNameByJobName(JobNames.LogCommunication) ?? '');            //OT: Quotation
    jobNameList.push(getListJobNameByJobName(JobNames.LogFax) ?? '');            //Utility: Fax
    jobNameList.push(getListJobNameByJobName(JobNames.LogMail) ?? '');            //Utility: Email
    jobNameList.push(getListJobNameByJobName(JobNames.LogPrint) ?? '');            //Utility: Print
    jobNameList.push(getListJobNameByJobName(JobNames.MemoList) ?? '');            //Utility: Memo/Task
    jobNameList.push(getListJobNameByJobName(JobNames.FileAttach) ?? '');            //Utility: Attached File
    jobNameList.push(getListJobNameByJobName(JobNames.RptCustomerShipment)??'');     //Shipment Status
    jobNameList.push(getListJobNameByJobName(JobNames.LogAmsIsf) ?? '');          //Utility: AMS/ISF Log
    //jobNameList.push(getListJobNameByJobName(JobNames.AllContainer) ?? '');    //All Container List

    for (let index = 0; index < jobNameList.length; index++) {
      if (tableId.includes('oiQuotation')) {
        if (jobNameList[index].includes('oeQuotation'))
          return true;
      }
      else if (tableId.includes('aiQuotation')) {
        if (jobNameList[index].includes('aeQuotation'))
          return true;
      }
      else if (tableId.includes(jobNameList[index]))
        return true;
    }

    return false;
  }

  const startCell = useRef<any>(null);
  const endCell = useRef<any>(null);

  function checkCellSelectArea() {
    const selectCellArr = document.querySelectorAll('.data-cell-selected-inner');
    if (selectCellArr && selectCellArr.length > 0) {
      const cellRange = selectCellArr.length - 1;

      const startCellId = selectCellArr[0].id.split('-');
      const endCellId = selectCellArr[cellRange].id.split('-');

      startCell.current = { row: parseInt(startCellId[startCellId.length - 2] ?? 0), column: parseInt(startCellId[startCellId.length - 1] ?? 0) };
      endCell.current = { row: parseInt(endCellId[endCellId.length - 2] ?? 0), column: parseInt(endCellId[endCellId.length - 1] ?? 0) };
      return true;
    } else {
      startCell.current = null
      endCell.current = null
      return false;
    }
  }

  function onFunction(fkey: string) {
    const isLookupTable = tableId.includes('-lookuptable');
    if (isLookupTable) return false;
    else {
      if (fkey === "F5") {
        const columnId = getCellId(tableId, getSelectedCol());
        if (columnId && !isNotUseStatusSearch() && tableId !== 'tbl-list-CTLST-opReport') {
          channel.publish('ifms-filter.preset', { columnId, value: getCellText(tableId, selectedRow.current, _selectedCol.current) });
          return true;
        }
        return true;
      }
    }

    if (fkey === "C+67") {
      if (checkCellSelectArea()) {
        // area로 선택 된 경우, 각 cell의 값을 text1 \t text2 \t test3 \n ... 과 같은 패턴으로 클립보드에 저장해야 함.
        var textarea = document.createElement('textarea');

        let startCol = startCell.current.column;
        let endCol = endCell.current.column;
        let startRow = startCell.current.row;
        let endRow = endCell.current.row;

        if (startCol > endCol) {
          startCol = endCell.current.column;
          endCol = startCell.current.column;
        }

        if (startRow > endRow) {
          startRow = endCell.current.row;
          endRow = startCell.current.row;
        }

        let fullText = '';

        for (var i = startRow; i <= endRow; i++) {
          for (var j = startCol; j <= endCol; j++) {
            let text = getCellText(tableId, i, j)?.toString() ?? "";
            fullText += text + (j !== endCol ? '\t' : '');
          }
          fullText += '\n';
        }

        textarea.value = fullText;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;

      } else {
        const columnId = getCellId(tableId, getSelectedCol());
        if (columnId) {
          let text = getCellText(tableId, selectedRow.current, _selectedCol.current)?.toString() ?? "";
          const isShipmentTable = tableId.includes('oceanImport') || tableId.includes('oceanExport') || tableId.includes('otherShipment');
          const isContainerColumn = columnId === 'containerNo' || columnId === 'cntrNo' || columnId === 'container';

          if (isShipmentTable && isContainerColumn) {
            text = getCellTooltip(tableId, selectedRow.current, _selectedCol.current)?.toString() ?? "";
          }

          if (window.isSecureContext) {
            navigator.clipboard.writeText(text)
              .then(() => { /* console.log("Copy Success"); */})
              .catch(() => {  /*console.log("Copy Fail"); */ });
            return true;
          } else {
            textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            return true;
          }
        }
      }
      return true;
    }

    return false;
  }

  function onEnterKey(evt: KeyboardEvent) {
    const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
    if (selectedRow.current < 0) return false;

    if (modals.length > 0) {
      return false;
    }
    // Check if event is coming from list filter box. If so ignore it to be handled properly.
    if (evt.target) {
      const evtNode = evt.target as Element;
      if (evtNode && evtNode.id && evtNode.id.startsWith('ifms-filter-')) {
        return false;
      }
    }
    //Top Bar Search시 Enter 키 입력했을때 이벤트가 같이 동작하여 막음
    const focusEle = document.activeElement;
    if (document.getElementById('ifms-search-text') === focusEle) {
      return false;
    }

    if (executeSameFuncDoubleClickAndEnter && selectedRow.current >= 0) {
      onRowDoubleClick(selectedRow.current);
       return true;
    }
    return false;
  }

  function setSelectedCol(c: any) {
    ColumnMemory[tableId] = c;
    _selectedCol.current = c;
  }

  function getSelectedCol() {
    return _selectedCol.current;
  }

  function getSelectedRow() {
    return selectedRow.current;
  }

  function initialize() {
    subscribeChannels();

    document.addEventListener('dblclick', onDoubleClick);
    document.addEventListener('click', onClick);
    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('keydown', onKeyDown);
    table?.addEventListener('scroll', onScroll);
  }

  function uninitialize() {
    unsubscribeChannels();

    document.removeEventListener('dblclick', onDoubleClick);
    document.removeEventListener('click', onClick);
    document.removeEventListener('contextmenu', onContextMenu);
    document.removeEventListener('keydown', onKeyDown);
    table?.removeEventListener('scroll', onScroll);
  }

  function onDoubleClick(e: MouseEvent): void {
    const clickedCell = findClickedCell(e, tableId);
    if (clickedCell) {
      const { row, column } = parseCellLocation(clickedCell.id);

      doSelectTableCellAndNotify(row, column, true);

      if (onRowDoubleClick) {
        onRowDoubleClick(row);
      }
    }
  }

  function onClick(e: MouseEvent) {
    const clickedCell = findClickedCell(e, tableId);
    if (clickedCell && document.getElementById(tableId)?.contains(e.target as Element)) {
      const { row, column } = parseCellLocation(clickedCell.id);
      doSelectTableCellAndNotify(row, column, true);
    }
  }

  function onContextMenu(e: MouseEvent) {
    const clickedCell = findClickedCell(e, tableId);
    if (clickedCell) {
      const { row, column } = parseCellLocation(clickedCell.id);

      doSelectTableCellAndNotify(row, column, true);

      if (onCellRightClick) {
        onCellRightClick(row, column, clickedCell, e);

        //Don't allow default browser context menu
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }

  function onScroll() {
    if (useVirtual) {
      updateScrollMemory(tableId)
      const selectedRowEle = table.getElementsByClassName('data-row-selected');

      if (selectedRowEle.length === 0) {
        setTimeout(() => {
          selectTableRow(selectedRow.current, true);
        }, 10);
      }
    }
  }

  function onKeyDown(e: KeyboardEvent) {
    const eventTarget: Element | null = e.target as (Element | null);
    if (eventTarget instanceof HTMLTextAreaElement && !table?.contains(eventTarget)) return;

    const isLookupTable = eventTarget && eventTarget.id.includes('-lookuptable');
    const isLookupField = eventTarget && eventTarget.classList.contains('ifms-lookup');
    
    if (isLookupField || isLookupTable) {
      if (tableId?.startsWith('tbl-list-')) return;
    } else if (scopedKeydown) {
      if (eventTarget !== table) return;
    }

    // let isLookupField = false;
    // if (eventTarget && eventTarget.classList.contains('ifms-tabable')) {
    //   isLookupField = eventTarget.classList.contains('ifms-lookup');
    //   if (!isLookupField) return;
    // }


    const reactModal = document.querySelector('[class^="ReactModal__Content"]');
    if (reactModal && !reactModal.contains(table)) return;

    let stopEvent = false;
    const columnId = getCellId(tableId, getSelectedCol());

    if (columnId) {
      //console.log(e.keyCode);
      if (e.ctrlKey && e.keyCode === 67) {
        onFunction("C+67");
      }
    }

    const arrowKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
    const listKeys = ["Home", "End", "PageUp", "PageDown"];

    if (arrowKeys.includes(e.key)) {
      const isHorizontal = e.key === "ArrowLeft" || e.key === "ArrowRight";

      if (!(isLookupField && isHorizontal)) {
        if (!isHorizontal || columnId) {
          stopEvent = true;
  
          if (isListView || useVirtual)
            handleKeyNavigation(e.key);
          else
            navigate(e.key);
        }
      }
    }
    else if (listKeys.includes(e.key)) {
      const needColumnId = e.key === "Home" || e.key === "End";

      if (!(isLookupField && needColumnId)) {
        if (!needColumnId || columnId) {
          stopEvent = true;
          handleKeyNavigation(e.key);
        }
      }
    }
    //List에서 컬럼을 클릭하고 tab을 눌렀을 경우 다음 셀로 이동하지 않은 문제가 있어서 추가 tikim 261007
    else if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
      const currentTable = document.getElementById(tableId);
      const isTableFocused = !!currentTable && eventTarget === currentTable;

      if (isListView && isTableFocused && columnId && selectedRow.current >= 0) {
        const numberOfHeader = document.getElementById(`${tableId}-header-row`)?.childElementCount ?? 0;
        const isLastCol = getSelectedCol() >= numberOfHeader - 1;

        if (!isLastCol) {
          stopEvent = true;
          handleKeyNavigation('ArrowRight');
        } else {
          const rowInfo = getSelectedRowInfo();
          if (rowInfo && !rowInfo.isLastIndex) {
            stopEvent = true;
            checkSelectedRowDisplayed(() => {
              const tableNode = document.getElementById(tableId);
              if (tableNode) tableNode.scrollLeft = 0; 
              doSelectTableCellAndNotify(selectedRow.current + 1, 0, false);
            });
          }
        }
      }
    }
    if (stopEvent) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
  
  function doNotifyRowChanged() {
    if (onRowIndexChanged) {
      onRowIndexChanged(selectedRow.current);
    }
  }

  function notifyRowChanged() {
    if (!!notifiationTimer) {
      clearTimeout(notifiationTimer);
    }
    doNotifyRowChanged();
    //notifiationTimer = setTimeout(doNotifyRowChanged, NotificationTimeout);
  }

  function navigate(keyCode: string) {
    if (!table || !table.offsetParent) {
      // Element is hidden
      return;
    }
    const numberOfHeader = document.getElementById(`${tableId}-header-row`)?.childElementCount;
    const rowgroupElements = table.querySelectorAll('[role="rowgroup"]');
    let dataLength = 0;
    rowgroupElements.forEach(function (rowgroupElement) {
      const rowElements = rowgroupElement.querySelectorAll('[role="row"]');
      dataLength += rowElements.length;
    });

    let newRowIndex = selectedRow.current;
    let newColIndex = getSelectedCol();

    if (keyCode === "ArrowUp") {
      newRowIndex = newRowIndex - 1;
      if (newRowIndex < 0) {
        return;
      }
    } else if (keyCode === "ArrowDown") {
      newRowIndex = newRowIndex + 1;
      if (dataLength === newRowIndex) return;
    } else if (keyCode === "ArrowLeft") {
      newColIndex = newColIndex - 1;
    } else if (keyCode === "ArrowRight") {
      newColIndex = newColIndex + 1;
      if (numberOfHeader && newColIndex >= numberOfHeader) return;
    }

    doSelectTableCellAndNotify(newRowIndex, newColIndex, false);
  }

  function checkSelectedRowDisplayed(callback: (result: { index: number, isLastIndex: boolean } | undefined) => void, scrollTopPos: number = -1) {
    const waitForSelectedRowDisplayed = (renderCallback: () => void) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(renderCallback);
      });
    };

    let returnData: { index: number, isLastIndex: boolean } | undefined = undefined;
    const selectedRowEle = table.getElementsByClassName('data-row-selected');

    if (selectedRowEle.length !== 0) {
      if (scrollTopPos !== -1) {
        table.scrollTo({ top: scrollTopPos });

        waitForSelectedRowDisplayed(() => {
          selectTableRow(selectedRow.current);
          returnData = getSelectedRowInfo();
          callback(returnData);
        });
      }
      else {
        returnData = getSelectedRowInfo();
        callback(returnData);
      }
    }
    else {
      let scrollToOptions = {
        top: ScrollMemory[tableId]?.selectedRowScrollPosition ?? 0,
      }

      table.scrollTo(scrollToOptions);

      waitForSelectedRowDisplayed(() => {
        selectTableRow(selectedRow.current);
        returnData = getSelectedRowInfo();
        callback(returnData);
      });
    }

    return returnData;
  }

  function getSelectedRowInfo() {
    if (!table) return;

    const row = table.querySelector('.data-row-selected') as HTMLElement
      ?? table.querySelector('.data-cell-selected')?.closest<HTMLElement>('[role="row"]');

    if (!row) return;

    const rows = Array.from(table.querySelectorAll<HTMLElement>('[role="row"]'));
    const index = rows.indexOf(row);

    if (index === -1) return;

    return {
      index,
      isLastIndex: index === rows.length - 1,
    };
  }

  function handleKeyNavigation(fkey: string) {
    if (!table || !table.offsetParent) {
      return; // Element is hidden
    }

    const handlePageKeys = (result: { index: number, isLastIndex: boolean } | undefined) => {
      if (!result) return;

      const isPageUp = fkey === 'PageUp';
      const moveRowCount = pageMoveCellCount(tableId, selectedRow.current, _selectedCol.current, isPageUp);

      if (!isPageUp && result.isLastIndex) return;

      const scrollTargetRow = selectedRow.current + (isPageUp ? 0 : moveRowCount);
      const cellId = `${tableId}-cell-${scrollTargetRow}-${_selectedCol.current}`;
      const cell = document.getElementById(cellId);
      const header = document.getElementById(`${tableId}-header-row`);

      if (cell && header) {
        const hrect = header.getBoundingClientRect();
        const scrollToOptions = {
          top: (cell as HTMLElement).offsetTop + hrect.height + (isPageUp ? - table.clientHeight : 0),
        }

        const findNextRow = () => {
          const newRow = selectedRow.current + (isPageUp ? - moveRowCount : moveRowCount);
          doSelectTableCellAndNotify(newRow, _selectedCol.current, false);
        }
        checkSelectedRowDisplayed(findNextRow, scrollToOptions.top);
      }
    };

    const handleEndKey = (result: { index: number, isLastIndex: boolean } | undefined) => {
      if (!result) return;
      const numberOfHeader = document.getElementById(`${tableId}-header-row`)?.childElementCount;
      if (numberOfHeader && numberOfHeader > 0) {
        const lastColumnIndex = numberOfHeader - 1;
        const colSelected = doSelectTableCell(selectedRow.current, lastColumnIndex, false);
        if (colSelected) {
          setSelectedCol(lastColumnIndex);
        }
      }
    };

    const handleHomeKey = (result: { index: number, isLastIndex: boolean } | undefined) => {
      if (!result) return;
      const colSelected = doSelectTableCell(selectedRow.current, 0, false);
      if (colSelected) {
        setSelectedCol(0);
      }
    };

    const handleArrowKeys = (result: { index: number, isLastIndex: boolean } | undefined) => {
      if (!result) return;

      const numberOfHeader = document.getElementById(`${tableId}-header-row`)?.childElementCount ?? -1;
      let newRowIndex = selectedRow.current;
      let newColIndex = getSelectedCol();

      const movements: Record<string, () => boolean> = {
        'ArrowUp': () => {
          newRowIndex = newRowIndex - 1;
          return newRowIndex >= 0;
        },
        'ArrowDown': () => {
          newRowIndex = newRowIndex + 1;
          return !result.isLastIndex;
        },
        'ArrowLeft': () => {
          newColIndex = newColIndex - 1;
          return newColIndex >= 0;
        },
        'ArrowRight': () => {
          newColIndex = newColIndex + 1;
          return newColIndex < numberOfHeader;
        }
      };

      const movement = movements[fkey];
      if (movement()) {
        doSelectTableCellAndNotify(newRowIndex, newColIndex, false);
      }
    };

    const keyHandlers: Record<string, (result: { index: number, isLastIndex: boolean } | undefined) => void> = {
      'End': handleEndKey,
      'Home': handleHomeKey,
      'ArrowDown': handleArrowKeys,
      'ArrowUp': handleArrowKeys,
      'ArrowLeft': handleArrowKeys,
      'ArrowRight': handleArrowKeys,
      'PageDown': handlePageKeys,
      'PageUp': handlePageKeys,
    };

    const handler = keyHandlers[fkey];
    if (handler) {
      checkSelectedRowDisplayed(handler);
    }
  }

  function doSelectTableCellAndNotify(newRowIndex: number, newColIndex: number, fromClick: boolean) {
    const rowSelected = doSelectTableRow(newRowIndex, fromClick);
    if (rowSelected) {
      selectedRow.current = newRowIndex;

      doNotifyRowChanged();
    } else {
      if (fromClick && deselectable) {
        selectedRow.current = -1;
        doNotifyRowChanged();
      }
    }
    const colSelected = doSelectTableCell(newRowIndex, newColIndex, fromClick);
    if (colSelected) {
      setSelectedCol(newColIndex);
    }
  }

  function doSelectTableCell(row: number, col: number, holdScroll: boolean = false, isLookup: boolean = false) {
    if (row < 0 || col < 0) {
      return false;
    }

    const selectedCellClass = dontHighlightCell ? 'data-cell-selected-invisible' : 'data-cell-selected';

    const cellId = `${tableId}-cell-${row}-${col}`;
    const currentCellSelections = document.querySelectorAll(`.${selectedCellClass}`);

    if (currentCellSelections && currentCellSelections.length > 0) {
      if (currentCellSelections[0].id === cellId) {
        // Required cell already selected
        return false;
      } else {
        currentCellSelections[0].classList.remove(selectedCellClass);
      }
    }

    const cell = document.getElementById(cellId);
    if (cell) {
      cell.classList.add(selectedCellClass);
      scrollCellIntoView(tableId, cell, holdScroll, iFMSChannel, useVirtual, isLookup);
      return true;
    }

    return false;
  }

  function doSelectTableRow(index: number, fromClick: boolean = false) {
    if (index < 0) {
      return false;
    }

    const rowId = `${tableId}-row-${index}`;
    const row = document.getElementById(rowId);
    if (!row) {
      // Invalid index
      return false;
    }

    const rowSelector = tableId ? '#' + tableId + ' .data-row-selected' : '.data-row-selected';

    const currentRowSelections = document.querySelectorAll(rowSelector);
    if (currentRowSelections && currentRowSelections.length > 0) {
      if (currentRowSelections[0].id === rowId) {
        // Required row already selected
        if (fromClick && deselectable) {
          currentRowSelections[0].classList.remove('data-row-selected');
          currentRowSelections[0].classList.add('data-row-selectable');
          return false;
        }
        return false;
      } else {
        currentRowSelections[0].classList.remove('data-row-selected');
        currentRowSelections[0].classList.add('data-row-selectable');
      }
    }

    if (row) {
      row.classList.remove('data-row-selectable');
      if(!doNotSetRowBackColor) row.classList.add('data-row-selected');
      return true;
    }

    return false;
  }

  function selectTableRow(row: number, headerClick: boolean = false, isLookup: boolean = false) {
    const selectedCol = getSelectedCol();
    selectedRow.current = row;
    doSelectTableRow(row);
    if (selectedCol === -1) {
      selectTableCol(getDefaultColumnSelectionIndex(tableId, tableColumnDefaultFocusHierarchy));
    }
    else {
      doSelectTableCell(row, selectedCol, headerClick, isLookup);
    }
  }

  function tableNavigationCleanup() {
    delete ColumnMemory[tableId];
    delete ScrollMemory[tableId];
  }

  function selectTableCol(c: number) {
    setSelectedCol(c);
    doSelectTableCell(selectedRow.current, getSelectedCol());
  }

  function clearTableSelection() {
    if(!table) return;
    
    const currentRowSelections = table.querySelectorAll('.data-row-selected');
    if (currentRowSelections && currentRowSelections.length > 0) {
      currentRowSelections[0].classList.remove('data-row-selected');
      currentRowSelections[0].classList.add('data-row-selectable');
    }

    const selectedCellClass = dontHighlightCell ? 'data-cell-selected-invisible' : 'data-cell-selected';

    const currentCellSelections = table.querySelectorAll(`.${selectedCellClass}`);

    if (currentCellSelections && currentCellSelections.length > 0) {
      currentCellSelections[0].classList.remove(selectedCellClass);
    }

    selectedRow.current = -1;
    _selectedCol.current = -1;
  }
  
  return {
    selectTableRow,
    tableNavigationCleanup,
    selectTableCol,
    triggerTableSearch: onFunction,
    clearTableSelection,
    getSelectedRow,
  };
}
